import type {ObjectBounds} from "#template/geometry-processing/bounds"
import type {InterfaceDescriptor} from "#template/interface-descriptors"
import type {SceneNodes} from "#template/interfaces/scene-object"
import type {LiveValueAccessorListEntry, TransformAccessorListEntry} from "#template/runtime-graph/nodes/compile-template"
import type {SolverData} from "#template/runtime-graph/nodes/solver/data"
import type {Inlet, Outlet} from "#template/runtime-graph/slots"
import {NotReady} from "#template/runtime-graph/slots"
import {TypeDescriptors} from "#template/runtime-graph/type-descriptors"
import type {NodeClassImpl} from "#template/runtime-graph/types"
import type {EvaluatedTemplateOutputs} from "#template/fundamental/evaluated-input"
import type {TemplateNode} from "#template/template-node/template-node"
import type {InfoNodes, InfoNodeId} from "#template/interfaces/info-nodes"

const TD = TypeDescriptors

export const runTemplateDescriptor = {
    compiledTemplate: TD.inlet(TD.Function<(instanceToBind: any) => () => void>()),
    ready: TD.outlet(TD.Boolean),
    preDisplayList: TD.outlet(TD.Array(TD.Identity<SceneNodes.SceneNode>())),
    displayList: TD.outlet(TD.Array(TD.Identity<SceneNodes.SceneNode>())),
    descriptorList: TD.outlet(TD.Array(TD.Identity<InterfaceDescriptor<unknown>>())),
    infoList: TD.outlet(TD.Array(TD.Identity<InfoNodes.InfoNode>())),
    solverData: TD.outlet(TD.Identity<SolverData>()),
    outputs: TD.outlet(TD.Identity<EvaluatedTemplateOutputs>()),
    bounds: TD.outlet(TD.Identity<ObjectBounds>()),
    dimensionGuideBounds: TD.outlet(TD.Identity<ObjectBounds>()),
    transformAccessorList: TD.outlet(TD.Array(TD.Identity<TransformAccessorListEntry>())),
    liveValueAccessorList: TD.outlet(TD.Array(TD.Identity<LiveValueAccessorListEntry>())),
    lookupByExternalIdPath: TD.outlet(TD.Function<(x: string[]) => TemplateNode | null>()),
    nodeToInfoMap: TD.outlet(TD.Map<TemplateNode, InfoNodeId>(TD.Identity())),
}

export class RunTemplate implements NodeClassImpl<typeof runTemplateDescriptor, typeof RunTemplate> {
    static descriptor = runTemplateDescriptor
    static uniqueName = "RunTemplate"
    compiledTemplate!: Inlet<(instanceToBind: any) => () => void>
    ready!: Outlet<boolean>
    preDisplayList!: Outlet<SceneNodes.SceneNode[]>
    displayList!: Outlet<SceneNodes.SceneNode[]>
    descriptorList!: Outlet<InterfaceDescriptor<unknown>[]>
    infoList!: Outlet<InfoNodes.InfoNode[]>
    solverData!: Outlet<SolverData>
    outputs!: Outlet<EvaluatedTemplateOutputs>
    bounds!: Outlet<ObjectBounds>
    dimensionGuideBounds!: Outlet<ObjectBounds>
    transformAccessorList!: Outlet<TransformAccessorListEntry[]>
    liveValueAccessorList!: Outlet<LiveValueAccessorListEntry[]>
    lookupByExternalIdPath!: Outlet<(x: string[]) => TemplateNode | null>
    nodeToInfoMap!: Outlet<Map<TemplateNode, InfoNodeId>>

    private _compiledTemplate: ((instanceToBind: any) => () => void) | null = null
    private _runCompiledTemplate: (() => void) | null = null

    run() {
        if (this.compiledTemplate === NotReady) return
        if (this.compiledTemplate !== this._compiledTemplate) {
            this._compiledTemplate = this.compiledTemplate
            if (this.compiledTemplate) {
                this._runCompiledTemplate = this.compiledTemplate(this)
            } else {
                this._runCompiledTemplate = null
            }
        }
        if (this._runCompiledTemplate) {
            this._runCompiledTemplate()
        }
    }
}
